import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ProductEditor from "@/pages/admin/ProductEditor";

const STAFF_ROLES = new Set(["admin", "shop_manager"]);

export default function ProductEditorPage({ onOpenAuth }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth();
    const isEdit = Boolean(id);
    const [initial, setInitial] = useState(null);
    const [loading, setLoading] = useState(isEdit);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        if (!isEdit) return;
        api
            .get(`/admin/products`)
            .then(({ data }) => {
                const found = (data || []).find((p) => p.id === id);
                if (!found) setNotFound(true);
                else setInitial(found);
            })
            .catch((err) => {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                setNotFound(true);
            })
            .finally(() => setLoading(false));
    }, [id, isEdit]);

    const goBack = () => navigate("/admin?tab=ecommerce");

    if (authLoading) {
        return <div className="min-h-screen flex items-center justify-center text-ink-700">Loading…</div>;
    }
    if (!user || !STAFF_ROLES.has(user.role)) {
        navigate("/", { replace: true });
        return null;
    }

    return (
        <div className="min-h-screen bg-paper-alt" data-testid="product-editor-page">
            <Navbar onOpenAuth={onOpenAuth} />
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
                <Button
                    variant="outline"
                    onClick={goBack}
                    className="rounded-full mb-5"
                    data-testid="product-editor-back-btn"
                >
                    <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Products
                </Button>

                <div className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6 md:p-8">
                    {isEdit && loading ? (
                        <div className="text-ink-700 py-8 text-center">Loading product…</div>
                    ) : isEdit && notFound ? (
                        <div className="text-ink-700 py-8 text-center">Product not found.</div>
                    ) : (
                        <ProductEditor
                            page
                            initial={initial}
                            onOpenChange={goBack}
                            onSaved={goBack}
                        />
                    )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
